import { ChargeCardEligibilityResultTypenames } from '@varo/graphql-schema-types';
import backgroundTextureLavender from 'assets/images/vxl/component/productIntroScreen/background-texture-lavender.png';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ImageSourcePropType, StyleSheet, View } from 'react-native';
import Animated, {
  interpolateColor,
  useAnimatedRef,
  useAnimatedStyle,
  useScrollOffset,
} from 'react-native-reanimated';
import { FeatureFlagKey } from 'services/statsig/types';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

import useGetChargeCardEligibility from './hooks/useGetChargeCardEligibility';
import MakeCreditBuildingContent from './MakeCreditBuildingContent';

const MakeCreditBuilding = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('believe');
  const { chargeCardEligibilityData, loading, errorModel } =
    useGetChargeCardEligibility();

  const { isFeatureEnabled } = useStatsig();
  const isBelieveDay0M1 = isFeatureEnabled(FeatureFlagKey.BELIEVE_DAY_0_M1);
  const isBelieveDay0M2 = isFeatureEnabled(FeatureFlagKey.BELIEVE_DAY_0_M2);
  const subcategory = isBelieveDay0M2
    ? 'believe_day_0_application'
    : 'believe_application';

  const isUserEligible =
    chargeCardEligibilityData?.__typename ===
    ChargeCardEligibilityResultTypenames?.ChargeCardEligibilitySuccess;
  const isUserNotEligible =
    chargeCardEligibilityData?.__typename ===
    ChargeCardEligibilityResultTypenames?.ChargeCardNotQualified;

  const handleLinkTextAnalytics = usePressableAnalytics({
    analyticsId: 'howApply',
    customAttributes: {
      vb_eligibility: isUserEligible ? 'true' : 'false',
      subcategory,
      vb_day_0_eligibility: isBelieveDay0M2 ? 'true' : 'false',
    },
  });

  useViewDidFocusScreenAnalyticsEvent(
    chargeCardEligibilityData?.__typename
      ? {
          key: 'believe-apply',
          customAttributes: {
            vb_eligibility: isUserEligible ? 'true' : 'false',
            subcategory: isBelieveDay0M1
              ? 'believe_day_0_application'
              : 'believe_application',
          },
        }
      : null,
  );

  const handleApplyNowAnalytics = usePressableAnalytics({
    analyticsId: 'applyNow',
    customAttributes: {
      vb_eligibility: isUserEligible ? 'true' : 'false',
      cta: 'Apply Now',
      subcategory,
    },
  });

  const scrollRef = useAnimatedRef<Animated.ScrollView>();
  const scrollOffset = useScrollOffset(scrollRef);

  const navBarAnimatedStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      scrollOffset.value,
      [190, 200],
      ['transparent', theme.color.background.primary],
    ),
  }));

  const redirectUser = () => {
    if (isUserEligible) {
      handleApplyNowAnalytics();
      router.push('/believe/apply/cardApplication');
    }
    if (isUserNotEligible) {
      handleLinkTextAnalytics();
      router.push('/believe/apply/qualify');
    }
  };

  return (
    <View style={styles.parentContainer}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          headerTransparent: true,
          header: (props: NativeStackHeaderProps) => (
            <Animated.View style={navBarAnimatedStyle}>
              <ExpoNavigationBar {...props} transparent />
            </Animated.View>
          ),
        }}
      />
      <ProductIntroScreen
        headlineText={translatedText(
          'application.makeCreditBuilding.creditBuildingHeading',
        )}
        headlineTextStyle="expressive"
        backgroundTexture={backgroundTextureLavender as ImageSourcePropType}
        primaryButton={{
          label: isUserEligible
            ? translatedText('application.makeCreditBuilding.applyNow')
            : translatedText('application.makeCreditBuilding.howToApply'),
          onPress: redirectUser,
        }}
        visual={
          isBelieveDay0M2
            ? 'BELIEVE_DARK_CARD_A_ANIMATED'
            : 'BELIEVE_CARD_A_ANIMATED'
        }
        bodyContent={<MakeCreditBuildingContent />}
        topPadding={80}
        animatedRef={scrollRef}
      />
      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          errorModel && !loading ? <ErrorScreen {...errorModel} /> : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  parentContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default MakeCreditBuilding;
